import { I18n } from '@calendly/i18n';
import { CheckboxInput } from '@calendly/ui/components/checkbox-input';
import { AdminIcon, AlertWarningTriangleIcon, HideIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorIconWarning, colorTextStandard, colorTextSecondary } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useGetOrganizationQuery } from 'common';
import { useDistinctEventTypeProfiles } from 'hooks/useDistinctEventTypeProfiles';
import { formatDuration } from 'services/event_types';
import { snakeize } from 'services/string_utils';
import { AvatarListSection } from './avatar_list_section';
import { EventTypeInfo } from './event_type_info';
const containerIsPhoneCss = "csq0jyj";
const containerCss = "c18e8hyz";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const styledCheckboxInputCss = "s1t183h2";
const StyledCheckboxInput = forwardRef((props, ref) => <CheckboxInput ref={ref} {...props} className={classNames(styledCheckboxInputCss, props.className)} />);
const sectionCss = "s1lz4prh";
const Section = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(sectionCss, props.className)} />;
});
const sideCapIsDisabledCss = "su590q1";
const sideCapIsSelectedCss = "s127okmj";
const sideCapCss = "svj5tmj";
const SideCap = forwardRef((props, ref) => {
  const {
    isSelected,
    as: AsComponent,
    color,
    disabled,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const style = disabled ? null : {
    backgroundColor: color
  };
  const isDisabled = disabled;
  return <Component ref={ref} {...restProps} style={style} className={classNames(sideCapCss, {
    [sideCapIsDisabledCss]: isDisabled,
    [sideCapIsSelectedCss]: isSelected
  }, props.className)} />;
});
const detailsCss = "dkittms";
const Details = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(detailsCss, props.className)} />;
});
const nameContainerCss = "n12h0vm8";
const NameContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(nameContainerCss, props.className)} />;
});
const iconsWrapperCss = "i173ozgp";
const IconsWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(iconsWrapperCss, props.className)} />;
});
const membersCss = "mzt64zo";
const Members = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(membersCss, props.className)} />;
});
export const EventTypeItem = ({
  item,
  isSelected,
  isMultipleSelect = true,
  onClick,
  isLoading,
  disabled,
  ...props
}) => {
  const eventType = snakeize(item);
  const {
    owner_type: ownerType,
    duration_minutes: durationMinutes,
    duration_options: durationOptions,
    kind_description: kindDescription,
    location_type: locationType,
    location_types_count: locationTypesCount,
    pooling_type: poolingType = null
  } = eventType;
  const {
    profiles
  } = useDistinctEventTypeProfiles({
    eventType
  });
  const ppToLpMigrationStatusEnabled = useGetOrganizationQuery()?.data?.pp_to_lp_migration_status_enabled;
  const isSharedEventType = ownerType === 'Organization';
  const hideSecretIcon = ownerType === 'User' && ppToLpMigrationStatusEnabled || isSharedEventType;
  const formattedDuration = formatDuration(durationMinutes, undefined, true);
  const hasAvatarList = poolingType && profiles.length > 0;
  const textColor = item.active ? colorTextStandard : colorTextSecondary;
  const isDisabled = disabled || isLoading;
  return <label id={`event-type-label-${item.uuid}`}>
      <StyledCheckboxInput {...props} disabled={isDisabled} variant="selectable" checked={isSelected} onChange={e => {
      onClick(e);
    }} showControl={isMultipleSelect} singleLine label={<>
            <SideCap color={item.color} disabled={!item.active} isSelected={isSelected} />
            <Container>
              <Section>
                <Details>
                  <NameContainer>
                    <BodyText weight="bold" color={item.active ? 'standard' : 'muted'}>
                      {item.name}
                    </BodyText>
                    <IconsWrapper>
                      {!!item.parent_id && <AdminIcon color={textColor} title={item.is_user_group_managed ? I18n.t('publisher.organization.managed_event_types.group_managed_badge') : I18n.t('publisher.organization.managed_event_types.admin_managed_badge')} />}
                      {!item.public && !hideSecretIcon && <HideIcon color={textColor} title={I18n.t('publisher.landing_pages.edit.event_types_picker.secret_notice')} />}
                      {!item.active && <AlertWarningTriangleIcon color={colorIconWarning} title={I18n.t('publisher.landing_pages.edit.event_types_picker.inactive_notice')} />}

                    </IconsWrapper>
                  </NameContainer>
                  <EventTypeInfo duration={formattedDuration} durationOptions={durationOptions} kindDescription={kindDescription} locationType={locationType} locationTypesCount={locationTypesCount} />
                </Details>
              </Section>
              {hasAvatarList && <Members>
                  <AvatarListSection active={item.active} profiles={profiles} />
                </Members>}
            </Container>
          </>} />
    </label>;
};

require("./event_type_item.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./event_type_item.js");